import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Grid,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Collapse,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SaveIcon from "@mui/icons-material/Save";
import useRenderEngine from "./components/useRenderEngine";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import TableChartIcon from "@mui/icons-material/TableChart";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EmailIcon from "@mui/icons-material/Email";
import { toast } from "react-toastify";
import api from "../../conf/api";
import SecondTemplateRow from "./components/SecondTemplateRow";
import { SECOND_SLOT_PLATFORMS, slotOf } from "./BrandPublish/hooks/templateSlots";
import {
  PLATFORM_CATEGORIES,
  PLATFORM_EXTRA_CONFIG_SCHEMA,
  PLATFORMS_WITH_SETTINGS,
} from "./Enums/platform_constants";

const ALL_PLATFORMS = ["YOUTUBE", "TIKTOK", "INSTAGRAM", "FACEBOOK", "PINTEREST", "SNAPCHAT", "THREADS", "DAILYMOTION", "MSN", "OTHERS"];
const ORIENTATIONS = ["vertical", "horizontal"];

const DEFAULT_REPHRASE_PROMPT =
  "You are a creative video metadata writer.\n\n" +
  "Rephrase the given content using noticeably different wording while preserving the exact same meaning, context, and intent.\n" +
  "The result should feel like it was written by a different person, not a light edit.\n\n" +
  "Requirements:\n" +
  "- Keep the tone, style, and emotional intent the same.\n" +
  "- Do not reuse the original phrasing or sentence structure.\n" +
  "- Maintain all brand names, product names, proper nouns, and technical terms exactly as provided.\n" +
  "- Ensure the output sounds natural, fluent, and engaging.\n" +
  "- Do not add new information or remove any meaning.\n\n" +
  "Output rules:\n" +
  "- Return only valid JSON.\n" +
  "- Do not include markdown, comments, or explanations.\n" +
  "- Do not include any extra text outside the JSON.";

function PlatformPromptsSection({ brandName }) {
  const [open, setOpen] = useState(false);
  const [prompts, setPrompts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !brandName) return;
    setLoading(true);
    api
      .get(`/mrss/brand-platform-prompts/?brand=${encodeURIComponent(brandName)}`)
      .then((res) => {
        const data = res.data?.results ?? res.data ?? [];
        // Pre-fill every platform with the default prompt so the user can see
        // and edit it immediately. Saved custom prompts override the default.
        const map = {};
        ALL_PLATFORMS.forEach((p) => { map[p] = DEFAULT_REPHRASE_PROMPT; });
        data.forEach((item) => {
          if (item.rephrase_prompt) map[item.platform] = item.rephrase_prompt;
        });
        setPrompts(map);
      })
      .catch(() => toast.error("Failed to load platform prompts"))
      .finally(() => setLoading(false));
  }, [open, brandName]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only persist prompts that differ from the default — saves that equal the
      // default are stored as empty string so the backend falls back gracefully.
      const promptsList = ALL_PLATFORMS.map((p) => ({
        platform: p,
        rephrase_prompt: (prompts[p] || "") === DEFAULT_REPHRASE_PROMPT ? "" : (prompts[p] || ""),
      }));
      await api.post("/mrss/brand-platform-prompts/bulk_upsert/", {
        brand: brandName,
        prompts: promptsList,
      });
      toast.success("Platform prompts saved");
    } catch {
      toast.error("Failed to save platform prompts");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box mt={3}>
      <Divider />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        mb={1}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Platform Rephrase Prompts
        </Typography>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={open}>
        {loading ? (
          <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Set default AI system prompts per platform. These are used in Step 4 (Rephrase
              Content) of the brand publish wizard. Leave blank to use the global default.
            </Typography>

            {ALL_PLATFORMS.map((platform) => (
              <Box key={platform} mb={2}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  {platform}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  placeholder={DEFAULT_REPHRASE_PROMPT}
                  value={prompts[platform] || ""}
                  onChange={(e) =>
                    setPrompts((prev) => ({ ...prev, [platform]: e.target.value }))
                  }
                  sx={{
                    "& .MuiInputBase-root": { fontFamily: "monospace", fontSize: "0.8rem" },
                  }}
                />
              </Box>
            ))}

            <Button
              variant="contained"
              size="small"
              startIcon={
                saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
              }
              onClick={handleSave}
              disabled={saving}
              sx={{ mt: 1 }}
            >
              {saving ? "Saving…" : "Save Prompts"}
            </Button>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

function PlatformTemplatesSection({ brandName }) {
  const [open, setOpen] = useState(false);
  const [brandTemplates, setBrandTemplates] = useState([]);
  const [editorTemplates, setEditorTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  // Templates come from whichever render engine CreatorVault runs on
  // (RENDER_ENGINE on the backend); the backend keeps assignments per engine
  // and lists only the active engine's.
  const { engine } = useRenderEngine(open);
  const templatesPath = engine === "creatomate" ? "/mrss/creatomate-templates/" : "/mrss/editor-templates/";

  useEffect(() => {
    if (!open || !brandName || !engine) return;
    setLoading(true);
    Promise.all([
      api.get(`/mrss/brand-templates/?brand=${encodeURIComponent(brandName)}`),
      api.get(templatesPath),
    ])
      .then(([btRes, etRes]) => {
        setBrandTemplates(btRes.data?.results ?? btRes.data ?? []);
        setEditorTemplates(Array.isArray(etRes.data) ? etRes.data : []);
      })
      .catch(() => toast.error("Failed to load template data"))
      .finally(() => setLoading(false));
  }, [open, brandName, engine, templatesPath]);

  // The main row is the one this table always managed; YouTube may also have a
  // "second" row (components/SecondTemplateRow.js, hooks/templateSlots.js).
  const getAssigned = (platform, orientation, slot = "main") =>
    brandTemplates.find((t) => t.platform === platform && t.orientation === orientation && slotOf(t) === slot) || null;

  // The server keeps exactly one default; after a change touching a platform
  // with two templates, read back what it decided.
  const reloadAssignments = () =>
    api
      .get(`/mrss/brand-templates/?brand=${encodeURIComponent(brandName)}`)
      .then((res) => setBrandTemplates(res.data?.results ?? res.data ?? []))
      .catch(() => {});

  const handleSetDefault = async (platform, orientation, row) => {
    const key = `${platform}_${orientation}_second`;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.patch(`/mrss/brand-templates/${row.id}/`, { is_default: true });
      await reloadAssignments();
      toast.success(`${row.template_name || row.template_id} is now the default ${platform} ${orientation} template`);
    } catch {
      toast.error("Failed to change the default template");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const handleAssign = async (platform, orientation, template, slot = "main") => {
    const key = slot === "main" ? `${platform}_${orientation}` : `${platform}_${orientation}_second`;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const existing = getAssigned(platform, orientation, slot);
      const twoSlots = SECOND_SLOT_PLATFORMS.includes(platform);
      // The gallery list carries no documents, so the snapshot always comes
      // from the template detail.
      // A VRE template's snapshot is its `document`, a Creatomate template's its `source`.
      let snapshot = null;
      if (template) {
        try {
          const detailRes = await api.get(`${templatesPath}${template.id}/`);
          snapshot = (engine === "creatomate" ? detailRes.data?.source : detailRes.data?.document) ?? null;
        } catch {
          // snapshot fetch failed, proceed without — the editor fetches fresh anyway
        }
      }

      const payload = {
        brand: brandName,
        platform,
        orientation,
        template_id: template?.id ?? "",
        template_name: template?.name ?? "",
        engine,
        template_snapshot: snapshot,
        ...(slot === "second" ? { slot } : {}),
      };

      if (existing) {
        if (!template) {
          await api.delete(`/mrss/brand-templates/${existing.id}/`);
          setBrandTemplates((prev) => prev.filter((t) => t.id !== existing.id));
          toast.success(`Cleared ${platform} ${orientation}`);
        } else {
          const res = await api.patch(`/mrss/brand-templates/${existing.id}/`, payload);
          setBrandTemplates((prev) => prev.map((t) => (t.id === existing.id ? res.data : t)));
          toast.success(`Updated ${platform} ${orientation}`);
        }
      } else if (template) {
        const res = await api.post("/mrss/brand-templates/", payload);
        setBrandTemplates((prev) => [...prev, res.data]);
        toast.success(`Assigned ${template.name} to ${platform} ${orientation}${slot === "second" ? " (second)" : ""}`);
      }
      if (twoSlots) await reloadAssignments();
    } catch {
      toast.error("Failed to save template assignment");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <Box mt={3}>
      <Divider />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        mb={1}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Platform Templates
        </Typography>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={open}>
        {loading ? (
          <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Platform</strong></TableCell>
                <TableCell><strong>Orientation</strong></TableCell>
                <TableCell><strong>Assigned Template</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ALL_PLATFORMS.flatMap((platform) =>
                ORIENTATIONS.map((orientation) => {
                  const key = `${platform}_${orientation}`;
                  const assigned = getAssigned(platform, orientation);
                  const currentTemplate = assigned
                    ? editorTemplates.find((t) => t.id === assigned.template_id) || {
                        id: assigned.template_id,
                        name: assigned.template_name,
                      }
                    : null;
                  const mainRow = (
                    <TableRow key={key}>
                      <TableCell>{platform}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{orientation}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Autocomplete
                            size="small"
                            options={editorTemplates}
                            getOptionLabel={(o) => o.name || o.id}
                            value={currentTemplate}
                            onChange={(_, newVal) => handleAssign(platform, orientation, newVal)}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            sx={{ minWidth: 220 }}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="No template" size="small" />
                            )}
                          />
                          {saving[key] && <CircularProgress size={16} />}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  if (!SECOND_SLOT_PLATFORMS.includes(platform)) return mainRow;
                  return [
                    mainRow,
                    <SecondTemplateRow
                      key={`${key}_second`}
                      platform={platform}
                      orientation={orientation}
                      main={assigned}
                      second={getAssigned(platform, orientation, "second")}
                      editorTemplates={editorTemplates}
                      saving={saving[`${key}_second`]}
                      onAssign={(t) => handleAssign(platform, orientation, t, "second")}
                      onSetDefault={(row) => handleSetDefault(platform, orientation, row)}
                    />,
                  ];
                })
              )}
            </TableBody>
          </Table>
        )}
      </Collapse>
    </Box>
  );
}

function PlatformCategoryDefaultsSection({ brandName }) {
  const [open, setOpen] = useState(false);
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !brandName) return;
    setLoading(true);
    api
      .get(`/mrss/brand-platform-configs/?brand=${encodeURIComponent(brandName)}`)
      .then((res) => {
        const data = res.data?.results ?? res.data ?? [];
        const map = {};
        data.forEach((item) => {
          map[item.platform] = {
            category: item.category || "",
            extra_config: item.extra_config || {},
          };
        });
        setConfigs(map);
      })
      .catch(() => toast.error("Failed to load platform category defaults"))
      .finally(() => setLoading(false));
  }, [open, brandName]);

  const updateConfig = (platform, patch) =>
    setConfigs((prev) => ({
      ...prev,
      [platform]: { category: "", extra_config: {}, ...(prev[platform] || {}), ...patch },
    }));

  const updateExtra = (platform, field, value) =>
    setConfigs((prev) => {
      const existing = prev[platform] || { category: "", extra_config: {} };
      return {
        ...prev,
        [platform]: { ...existing, extra_config: { ...(existing.extra_config || {}), [field]: value } },
      };
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const configList = PLATFORMS_WITH_SETTINGS
        .map((platform) => ({
          platform,
          category: configs[platform]?.category || "",
          extra_config: configs[platform]?.extra_config || {},
        }))
        .filter((c) => c.category || Object.keys(c.extra_config).length > 0);

      await api.post("/mrss/brand-platform-configs/bulk_upsert/", {
        brand: brandName,
        configs: configList,
      });
      toast.success("Platform category defaults saved");
    } catch {
      toast.error("Failed to save platform category defaults");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box mt={3}>
      <Divider />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        mb={1}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Platform Category Defaults
        </Typography>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={open}>
        {loading ? (
          <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Set default categories and publishing options per platform. These pre-populate
              Step 3 of the Brand Publish wizard and can be overridden per video.
            </Typography>

            {PLATFORMS_WITH_SETTINGS.map((platform) => {
              const categories = PLATFORM_CATEGORIES[platform] || [];
              const extraSchema = PLATFORM_EXTRA_CONFIG_SCHEMA[platform] || {};
              const cfg = configs[platform] || { category: "", extra_config: {} };
              const extraCfg = cfg.extra_config || {};

              return (
                <Box key={platform} mb={2.5}>
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    {platform}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    {categories.length > 0 && (
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Channel / Category</InputLabel>
                        <Select
                          label="Channel / Category"
                          value={cfg.category || ""}
                          onChange={(e) => updateConfig(platform, { category: e.target.value })}
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {categories.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {Object.entries(extraSchema).map(([field, schema]) => {
                      if (schema.type === "bool") {
                        return (
                          <FormControlLabel
                            key={field}
                            control={
                              <Switch
                                size="small"
                                checked={field in extraCfg ? Boolean(extraCfg[field]) : schema.default}
                                onChange={(e) => updateExtra(platform, field, e.target.checked)}
                              />
                            }
                            label={<Typography variant="caption">{schema.label}</Typography>}
                          />
                        );
                      }
                      if (schema.type === "select") {
                        return (
                          <FormControl key={field} size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>{schema.label}</InputLabel>
                            <Select
                              label={schema.label}
                              value={extraCfg[field] ?? schema.default}
                              onChange={(e) => updateExtra(platform, field, e.target.value)}
                            >
                              {schema.options.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      }
                      return null;
                    })}
                  </Box>
                </Box>
              );
            })}

            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ mt: 1 }}
            >
              {saving ? "Saving…" : "Save Defaults"}
            </Button>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

// ── Google Sheets Auth Panel ──────────────────────────────────────────────────
function GoogleSheetsAuthPanel() {
  const [authStatus, setAuthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = () => {
    setLoading(true);
    api.get("/mrss/google-sheets/auth/")
      .then(r => setAuthStatus(r.data))
      .catch(() => setAuthStatus({ configured: false, is_authenticated: false }))
      .finally(() => setLoading(false));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setUploading(true);
      try {
        const json = JSON.parse(ev.target.result);
        await api.post("/mrss/google-sheets/auth/", { credentials_json: json });
        toast.success("Service account credentials saved — click Verify to test");
        fetchStatus();
      } catch (err) {
        toast.error(err.response?.data?.error || "Failed to save credentials");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsText(file);
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      await api.post("/mrss/google-sheets/verify/");
      toast.success("Google Sheets authenticated successfully!");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || "Verification failed — check credentials");
      fetchStatus();
    } finally {
      setVerifying(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove Google Sheets service account credentials?")) return;
    try {
      await api.delete("/mrss/google-sheets/auth/");
      toast.success("Credentials removed");
      setAuthStatus({ configured: false, is_authenticated: false });
    } catch {
      toast.error("Failed to remove credentials");
    }
  };

  const isOk = authStatus?.is_authenticated;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mb: 3,
        borderColor: isOk ? "success.light" : authStatus?.configured ? "warning.light" : "divider",
        bgcolor: isOk ? "success.50" : "background.paper",
      }}
    >
      <input type="file" accept=".json" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />

      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        {/* Left: status */}
        <Box display="flex" alignItems="center" gap={1.5}>
          <TableChartIcon color={isOk ? "success" : "action"} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              Google Sheets Integration
            </Typography>
            {loading ? (
              <CircularProgress size={14} sx={{ mt: 0.5 }} />
            ) : authStatus?.configured ? (
              <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                <Chip
                  icon={isOk ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={isOk ? "Authenticated" : "Not verified"}
                  color={isOk ? "success" : "warning"}
                  size="small"
                />
                {authStatus.service_account_email && (
                  <Typography variant="caption" color="text.secondary">
                    {authStatus.service_account_email}
                  </Typography>
                )}
                {isOk && authStatus.last_verified && (
                  <Typography variant="caption" color="text.secondary">
                    · verified {new Date(authStatus.last_verified).toLocaleString()}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Upload a service account JSON key to enable spreadsheet publishing
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: actions */}
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {authStatus?.configured && (
            <>
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={handleVerify}
                disabled={verifying}
                startIcon={verifying ? <CircularProgress size={14} /> : <CheckCircleIcon />}
              >
                Verify
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={handleRemove}>
                Remove
              </Button>
            </>
          )}
          <Tooltip title="Upload a service account JSON from Google Cloud Console">
            <Button
              size="small"
              variant="contained"
              startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Uploading…" : authStatus?.configured ? "Replace JSON" : "Upload Service Account JSON"}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {!authStatus?.configured && !loading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          Create a service account in Google Cloud Console, enable Sheets + Drive APIs, download the JSON key,
          and share your target spreadsheets with the service account email. Then upload the JSON here.
        </Typography>
      )}
    </Paper>
  );
}


// ── Per-brand Google Sheet config ─────────────────────────────────────────────
function BrandSheetConfigSection({ brandName }) {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState({
    social_spreadsheet_id: "", social_spreadsheet_name: "", social_sheet_tab: "Sheet1",
    partner_spreadsheet_id: "", partner_spreadsheet_name: "", partner_sheet_tab: "Sheet1",
    partner_platforms: [],
  });
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [socialTabs, setSocialTabs] = useState([]);
  const [partnerTabs, setPartnerTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !brandName) return;
    setLoading(true);
    api.get(`/mrss/google-sheets/brand-config/${encodeURIComponent(brandName)}/`)
      .then(r => {
        setConfig(r.data);
        // Pre-load tabs for configured sheets
        if (r.data.social_spreadsheet_id) fetchTabs(r.data.social_spreadsheet_id, "social");
        if (r.data.partner_spreadsheet_id) fetchTabs(r.data.partner_spreadsheet_id, "partner");
      })
      .catch(() => toast.error("Failed to load sheet config"))
      .finally(() => setLoading(false));
  }, [open, brandName]);

  const loadSpreadsheets = async () => {
    setLoadingSheets(true);
    try {
      const r = await api.get("/mrss/google-sheets/spreadsheets/");
      setSpreadsheets(r.data.spreadsheets || []);
      if ((r.data.spreadsheets || []).length === 0) toast.info("No spreadsheets found — make sure the service account is authenticated and has been shared on your sheets.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to list spreadsheets — verify auth first");
    } finally {
      setLoadingSheets(false);
    }
  };

  const fetchTabs = async (spreadsheetId, type) => {
    if (!spreadsheetId) return;
    try {
      const r = await api.get(`/mrss/google-sheets/spreadsheets/${spreadsheetId}/tabs/`);
      const tabs = r.data.tabs || [];
      if (type === "social") setSocialTabs(tabs);
      else setPartnerTabs(tabs);
    } catch { /* ignore */ }
  };

  const handleSheetChange = (type, sheet) => {
    if (type === "social") {
      setConfig(c => ({ ...c, social_spreadsheet_id: sheet?.id || "", social_spreadsheet_name: sheet?.name || "", social_sheet_tab: "Sheet1" }));
      setSocialTabs([]);
      if (sheet?.id) fetchTabs(sheet.id, "social");
    } else {
      setConfig(c => ({ ...c, partner_spreadsheet_id: sheet?.id || "", partner_spreadsheet_name: sheet?.name || "", partner_sheet_tab: "Sheet1" }));
      setPartnerTabs([]);
      if (sheet?.id) fetchTabs(sheet.id, "partner");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/mrss/google-sheets/brand-config/${encodeURIComponent(brandName)}/`, config);
      toast.success("Sheet config saved");
    } catch {
      toast.error("Failed to save sheet config");
    } finally {
      setSaving(false);
    }
  };

  const SheetRow = ({ type, label }) => {
    const isPartner = type === "partner";
    const sheetId   = isPartner ? config.partner_spreadsheet_id   : config.social_spreadsheet_id;
    const sheetName = isPartner ? config.partner_spreadsheet_name : config.social_spreadsheet_name;
    const sheetTab  = isPartner ? config.partner_sheet_tab        : config.social_sheet_tab;
    const tabs      = isPartner ? partnerTabs : socialTabs;
    const currentSheet = spreadsheets.find(s => s.id === sheetId)
      || (sheetId ? { id: sheetId, name: sheetName || sheetId } : null);

    return (
      <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1.5 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
          {label}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Autocomplete
            size="small"
            options={spreadsheets}
            getOptionLabel={o => o.name}
            value={currentSheet}
            onChange={(_, val) => handleSheetChange(type, val)}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            noOptionsText={spreadsheets.length === 0 ? "Click 'Load Sheets' first" : "No match"}
            sx={{ minWidth: 220, flex: 1 }}
            renderInput={params => <TextField {...params} label="Spreadsheet" placeholder="Select…" size="small" />}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sheet Tab</InputLabel>
            <Select
              value={sheetTab || ""}
              label="Sheet Tab"
              onChange={e => setConfig(c => ({ ...c, [`${type}_sheet_tab`]: e.target.value }))}
            >
              {(tabs.length > 0 ? tabs : [sheetTab || "Sheet1"]).map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {sheetId && (
            <Chip
              icon={<CheckCircleIcon />}
              label="configured"
              color="success"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box mt={3}>
      <Divider />
      <Box
        display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={1}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen(o => !o)}
      >
        <Box display="flex" alignItems="center" gap={0.75}>
          <TableChartIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>Google Sheets Config</Typography>
        </Box>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={open}>
        {loading ? (
          <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              When a video is published to a feed of this brand, a row is appended to the matching spreadsheet.
              Columns: <strong>URL · Thumbnail URL · WGID · Title · Description · Tags · (empty) · Date Published</strong>
            </Typography>

            <Button
              size="small"
              variant="outlined"
              startIcon={loadingSheets ? <CircularProgress size={14} /> : <TableChartIcon />}
              onClick={loadSpreadsheets}
              disabled={loadingSheets}
              sx={{ mb: 2 }}
            >
              {loadingSheets ? "Loading…" : "Load Sheets from Drive"}
            </Button>

            <SheetRow type="social"  label="Social Feeds Sheet"  />
            <SheetRow type="partner" label="Partner Feeds Sheet" />

            {/* Platform selector — only PARTNER feeds write sheet rows */}
            <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
                Platforms to track (Partner sheet)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                One sheet row is written per selected platform. The platform-specific video URL and thumbnail from the feed are used.
                If none selected, a single row with the main feed URL is written.
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={["DAILYMOTION", "MSN", "OTHERS"]}
                value={config.partner_platforms || []}
                onChange={(_, val) => setConfig(c => ({ ...c, partner_platforms: val }))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={params => (
                  <TextField {...params} label="Platforms" placeholder="Select platforms…" size="small" />
                )}
              />
            </Box>

            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ mt: 0.5 }}
            >
              {saving ? "Saving…" : "Save Sheet Config"}
            </Button>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}


/**
 * Brands Management Page
 * CRUD operations for brands
 */
function BrandsPreview() {
  const [brands, setBrands] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    default_language: null,
    copyright: "",
    author: "",
    website_url: "",
    disabled: false,
  });

  useEffect(() => {
    fetchBrands();
    fetchLanguages();
  }, [paginationModel.page, paginationModel.pageSize]);

  // Set English as default language when languages are loaded
  useEffect(() => {
    if (languages.length > 0 && !formData.default_language) {
      const englishLang = languages.find(l => l.name === "English");
      if (englishLang) {
        setFormData(prev => ({
          ...prev,
          default_language: englishLang.id
        }));
      }
    }
  }, [languages]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await api.get("/mrss/brands/", {
        params: {
          page: paginationModel.page + 1, // API uses 1-based indexing
          page_size: paginationModel.pageSize,
        },
      });
      setBrands(response.data.results || response.data);
      setRowCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await api.get("/youtube/languages/");
      setLanguages(response.data);
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  const handleOpenDialog = (brand = null) => {
    if (brand) {
      setEditingBrand(brand);
      setFormData({
        name: brand.name,
        description: brand.description || "",
        default_language: brand.default_language?.id || languages.find(l => l.name === "English")?.id || null,
        copyright: brand.copyright || "",
        author: brand.author || "",
        website_url: brand.website_url || "",
        disabled: brand.disabled,
        partner_email_template_id: brand.partner_email_template_id || "",
        partner_email_subject: brand.partner_email_subject || "",
        partner_email_body: brand.partner_email_body || "",
      });
    } else {
      setEditingBrand(null);
      const englishLang = languages.find(l => l.name === "English");
      setFormData(pre => ({
        name: "",
        description: "",
        default_language: englishLang?.id || null,
        copyright: "",
        author: "",
        website_url: "",
        disabled: false,
        partner_email_template_id: "",
        partner_email_subject: "",
        partner_email_body: "",
      }));
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBrand(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        toast.warning("Brand name is required");
        return;
      }

      // Strip UI-only keys before sending
      const { _emailSectionOpen, ...payload } = formData;

      if (editingBrand) {
        await api.put(`/mrss/brands/${editingBrand.name}/`, payload);
        toast.success("Brand updated successfully");
      } else {
        await api.post("/mrss/brands/", payload);
        toast.success("Brand created successfully");
      }

      handleCloseDialog();
      fetchBrands();
    } catch (error) {
      console.error("Error saving brand:", error);
      toast.error(error.response?.data?.error || "Failed to save brand");
    }
  };

  const handleDelete = async (brandName) => {
    if (window.confirm(`Are you sure you want to delete brand "${brandName}"?`)) {
      try {
        await api.delete(`/mrss/brands/${brandName}/`);
        toast.success("Brand deleted successfully");
        fetchBrands();
      } catch (error) {
        console.error("Error deleting brand:", error);
        toast.error("Failed to delete brand");
      }
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Brand Name",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <BusinessIcon color="primary" fontSize="small" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "default_language",
      headerName: "Language",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value?.name || "Not set"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "feeds_count",
      headerName: "Feeds",
      flex: 0.5,
      align: "center",
      renderCell: (params) => (
        <Chip label={params.value} color="primary" size="small" />
      ),
    },
    {
      field: "subbrands_count",
      headerName: "Subbrands",
      flex: 0.5,
      align: "center",
      renderCell: (params) => (
        <Chip label={params.value} color="secondary" size="small" />
      ),
    },
    {
      field: "disabled",
      headerName: "Status",
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Disabled" : "Active"}
          color={params.value ? "error" : "success"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.name)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Brand Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your MRSS feed brands
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Brand
        </Button>
      </Box>

      <GoogleSheetsAuthPanel />

      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={brands}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.name}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={rowCount}
          disableSelectionOnClick
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBrand ? "Edit Brand" : "Create New Brand"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                disabled={!!editingBrand}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={languages}
                getOptionLabel={(option) => option.name}
                value={languages.find((l) => l.id === formData.default_language) || null}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, default_language: newValue?.id || null })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Default Language" />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Author"
                value={formData.author}
                onChange={(e) =>
                  setFormData({ ...formData, author: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website URL"
                value={formData.website_url}
                onChange={(e) =>
                  setFormData({ ...formData, website_url: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Copyright"
                value={formData.copyright}
                onChange={(e) =>
                  setFormData({ ...formData, copyright: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.disabled}
                    onChange={(e) =>
                      setFormData({ ...formData, disabled: e.target.checked })
                    }
                  />
                }
                label="Disabled"
              />
            </Grid>
          </Grid>

          {editingBrand && (
            <>
              <PlatformCategoryDefaultsSection brandName={editingBrand.name} />
              <PlatformPromptsSection brandName={editingBrand.name} />
              <PlatformTemplatesSection brandName={editingBrand.name} />
              <BrandSheetConfigSection brandName={editingBrand.name} />

              {/* Partner Email Notification Config */}
              <Box mt={3}>
                <Divider />
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={2}
                  mb={1}
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => setFormData((f) => ({ ...f, _emailSectionOpen: !f._emailSectionOpen }))}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Partner Email Notification
                    </Typography>
                  </Box>
                  {formData._emailSectionOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
                <Collapse in={!!formData._emailSectionOpen}>
                  <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                    Configure email notification settings for partner feeds. When a video is deleted from a partner feed,
                    this template will be used to notify the partner. <strong>Email sending is not active yet.</strong>
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Template ID"
                        size="small"
                        value={formData.partner_email_template_id}
                        onChange={(e) => setFormData({ ...formData, partner_email_template_id: e.target.value })}
                        helperText="External email template identifier"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Subject"
                        size="small"
                        value={formData.partner_email_subject}
                        onChange={(e) => setFormData({ ...formData, partner_email_subject: e.target.value })}
                        helperText="Subject line for the notification email"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Body"
                        size="small"
                        multiline
                        rows={4}
                        value={formData.partner_email_body}
                        onChange={(e) => setFormData({ ...formData, partner_email_body: e.target.value })}
                        helperText="Message body for the notification email"
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingBrand ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BrandsPreview;
